import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { transformFiltersToPayload } from 'features/Yucollect/utils/util';
import { put, select, takeLatest } from 'redux-saga/effects';
import {
  setFooterSummary,
  setUniqueValues,
} from 'redux/slice/filter/Filter.slice';
import { RootReducerState } from 'redux/slice/reducer';
import {
  IAllocationDashboard,
  IChannelPlannerMetricsRequest,
  IChannelPlannerMetricsResponse,
  IGetAllocationDetailRequest,
  IGetAllocationDetailsResponse,
  IGetHeadersRequest,
  IGetHeadersResponse,
  IGetRepeatAllocationDashboardRequest,
  IGetRepeatAllocationDashboardResponse,
  IGetSearchInDataGridRequest,
  IGetSummaryRequest,
  IGetSummaryResponse,
  IGetUniqueValuesByColumnRequest,
  IGetUniqueValuesByColumnResponse,
  ISearchInFilterByValuesRequest,
} from 'redux/types/Allocation.types';
import { allocationEndpoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';

import {
  downloadAllocationExcelFile,
  downloadAllocationExcelFileFailure,
  downloadAllocationExcelFileSuccess,
  fetchAllocationDashboard,
  fetchAllocationDashboardFailure,
  fetchAllocationDashboardSuccess,
  fetchAllocationFileHeaders,
  fetchAllocationFileHeadersFailure,
  fetchAllocationFileHeadersSuccess,
  fetchAllocationFileSummary,
  fetchAllocationFileSummaryFailure,
  fetchAllocationFileSummarySuccess,
  fetchAllocationRecordsById,
  fetchAllocationRecordsByIdFailure,
  fetchAllocationRecordsByIdSuccess,
  fetchAutoAssignStatus,
  fetchAutoAssignStatusFailure,
  fetchAutoAssignStatusSuccess,
  fetchChannelPlannerMetrics,
  fetchChannelPlannerMetricsFailure,
  fetchChannelPlannerMetricsSuccess,
  fetchRepeatAllocationDashboard,
  fetchRepeatAllocationDashboardFailure,
  fetchRepeatAllocationDashboardSuccess,
  getUniqueValuesByColumnName,
  getUniqueValuesByColumnNameFailure,
  getUniqueValuesByColumnNameSuccess,
  mergeHistoricData,
  mergeHistoricDataFailure,
  mergeHistoricDataSuccess,
  searchInDataGrid,
  searchInFilterByValues,
  searchInFilterByValuesFailure,
  searchInFilterByValuesSuccess,
  setAllocationFileRecordIds,
} from '../../slice/allocation/AllocationDetails.slice';

// Saga for fetching allocation records by ID
function* fetchAllocationRecordsByIdSaga(
  action: PayloadAction<IGetAllocationDetailRequest>
) {
  try {
    const {
      allocationId,
      isClosed = false,
      isValid = true,
      limit = 10,
      offset = 0,
      columns,
    } = action.payload;
    const response: AxiosResponse<IGetAllocationDetailsResponse> =
      yield HTTPClient.PostRequest(
        `${allocationEndpoints.GET_ALLOCATION_DETAILS(
          allocationId,
          isClosed,
          isValid,
          limit,
          offset
        )}`,
        { columns }
      );
    yield put(fetchAllocationRecordsByIdSuccess(response.data));
  } catch (error: any) {
    yield put(fetchAllocationRecordsByIdFailure(error?.message));
    message.error('Failed to fetch allocation records');
  }
}
// Saga for fetching allocation file headers
function* fetchAllocationFileHeadersSaga(
  action: PayloadAction<IGetHeadersRequest>
) {
  try {
    const { allocationId } = action.payload;
    const response: AxiosResponse<IGetHeadersResponse> =
      yield HTTPClient.GetRequest(
        `${allocationEndpoints.GET_ALLOCATION_DETAIL_HEADERS(allocationId)}`
      );
    yield put(fetchAllocationFileHeadersSuccess(response.data));
    // message.success('Allocation file headers fetched successfully');
  } catch (error: any) {
    yield put(fetchAllocationFileHeadersFailure(error?.message));
    message.error('Failed to fetch allocation file headers');
  }
}

// Saga for fetching allocation summary
function* fetchAllocationDashboardSaga(
  action: PayloadAction<IAllocationDashboard>
) {
  try {
    const { allocationId } = action.payload;
    const response: AxiosResponse<IGetHeadersResponse> =
      yield HTTPClient.GetRequest(
        `${allocationEndpoints.ALLOCATION_SUMMARY(allocationId)}`
      );
    yield put(fetchAllocationDashboardSuccess(response.data));
    // message.success('Allocation file headers fetched successfully');
  } catch (error: any) {
    yield put(fetchAllocationDashboardFailure(error?.message));
    message.error('Failed to fetch allocation dashboard');
  }
}

// Saga for fetching repeat allocation summary
function* fetchRepeatAllocationDashboardSaga(
  action: PayloadAction<IGetRepeatAllocationDashboardRequest>
) {
  try {
    const { allocationId, agencyId, allocatorId } = action.payload;
    const response: AxiosResponse<IGetRepeatAllocationDashboardResponse> =
      yield HTTPClient.GetRequest(
        `${allocationEndpoints.REPEAT_ALLOCATION_DASHBOARD(
          allocationId,
          agencyId,
          allocatorId
        )}`
      );
    yield put(fetchRepeatAllocationDashboardSuccess(response.data));
    // message.success('Allocation file headers fetched successfully');
  } catch (error: any) {
    yield put(fetchRepeatAllocationDashboardFailure(error?.message));
    message.error('Failed to fetch allocation dashboard');
  }
}

// Saga for fetching allocation file summary
function* fetchAllocationFileSummarySaga(
  action: PayloadAction<IGetSummaryRequest>
) {
  try {
    const {
      allocationId,
      isClosed = false,
      isValid = true,
      filters,
      isGlobalSearch = false,
      globalSearchValue = '',
    } = action.payload;
    const response: AxiosResponse<IGetSummaryResponse> =
      yield HTTPClient.PostRequest(
        `${allocationEndpoints.GET_ALLOCATION_DETAIL_SUMMARY(
          allocationId,
          isClosed,
          isValid
        )}`,
        { filters, isGlobalSearch, globalSearchValue }
      );
    yield put(fetchAllocationFileSummarySuccess(response.data));
    yield put(setFooterSummary(response.data.data));
  } catch (error: any) {
    yield put(fetchAllocationFileSummaryFailure(error?.message));
    message.error('Failed to fetch allocation file summary');
  }
}

// Saga for getting unique values by column name
function* getUniqueValuesByColumnNameSaga(
  action: PayloadAction<IGetUniqueValuesByColumnRequest>
) {
  const {
    columnName,
    allocationId,
    isClosed = false,
    isValid = true,
    filters,
    limit = 10,
    offset = 0,
  } = action.payload;
  try {
    const response: AxiosResponse<IGetUniqueValuesByColumnResponse> =
      yield HTTPClient.PostRequest(
        `${allocationEndpoints.GET_UNIQUE_VALUES_BY_COLUMN(
          allocationId,
          isClosed,
          isValid,
          limit,
          offset
        )}`,
        { columnName, filters }
      );

    yield put(getUniqueValuesByColumnNameSuccess(response.data));
    yield put(setUniqueValues(response.data.data));
    // Special handling for allocationRecordId
    if (columnName === 'allocationRecordId') {
      yield put(setAllocationFileRecordIds(response.data.data));
    }
    // message.success('Unique values fetched successfully');
  } catch (error: any) {
    yield put(getUniqueValuesByColumnNameFailure(error?.message));
    message.error('Failed to fetch unique values');
  }
}

// Saga for searching in filter by values
function* searchInFilterByValuesSaga(
  action: PayloadAction<ISearchInFilterByValuesRequest>
) {
  try {
    const {
      columnName,
      columnType,
      columnValue,
      allocationId,
      isClosed = false,
      isValid = true,
    } = action.payload;
    const response: AxiosResponse<IGetUniqueValuesByColumnResponse> =
      yield HTTPClient.PostRequest(
        `${allocationEndpoints.SEARCH_IN_FILTER_BY_VALUES(
          allocationId,
          isClosed,
          isValid
        )}`,
        { columnName, columnType, columnValue }
      );
    yield put(searchInFilterByValuesSuccess(response.data));
    yield put(setUniqueValues(response.data.data));
    // message.success('Search results fetched successfully');
  } catch (error: any) {
    yield put(searchInFilterByValuesFailure(error?.message));
    message.error('Failed to fetch search results');
  }
}
// Saga for merging historic data
function* mergeHistoricDataSaga(
  action: PayloadAction<{ allocationId: string }>
) {
  try {
    const { allocationId } = action.payload;
    // define interface for response
    const response: AxiosResponse<any> = yield HTTPClient.GetRequest(
      `${allocationEndpoints.MERGE_HISTORIC_DATA(allocationId)}`
    );
    yield put(mergeHistoricDataSuccess(response.data));
    // message.success(response.data.message);

    // Calling data grid
    const fetchAllocationRecordsByIdAction = {
      type: fetchAllocationRecordsById.type,
      payload: {
        allocationId,
        columns: [],
      },
    };
    yield put(fetchAllocationRecordsByIdAction); // Dispatch the action
    // Calling data grid headers
    const fetchAllocationFileHeadersAction = {
      type: fetchAllocationFileHeaders.type,
      payload: {
        allocationId,
      },
    };
    yield put(fetchAllocationFileHeadersAction); // Dispatch the action
  } catch (error: any) {
    yield put(mergeHistoricDataFailure(error?.message));
    message.error('Failed to merge historic data');
  }
}

// Saga for searching in data grid
function* searchInDataGridSaga(
  action: PayloadAction<IGetSearchInDataGridRequest>
) {
  try {
    const selectFilters = (state: RootReducerState) => state.filter;
    const { appliedFilters } = yield select(selectFilters);
    const {
      allocationId,
      isClosed = false,
      isValid = true,
      limit = 10,
      offset = 0,
      value,
    } = action.payload;
    const response: AxiosResponse<IGetAllocationDetailsResponse> =
      yield HTTPClient.PostRequest(
        `${allocationEndpoints.SEARCH_IN_DATAGRID(
          allocationId,
          isClosed,
          isValid,
          limit,
          offset
        )}`,
        {
          value,
          filters: { columns: transformFiltersToPayload(appliedFilters) },
        }
      );
    yield put(fetchAllocationRecordsByIdSuccess(response.data));
    // Call the fetchAllocationFileSummary with required parameters
    const fetchAllocationFileSummaryAction = {
      type: fetchAllocationFileSummary.type,
      payload: {
        filters: { columns: transformFiltersToPayload(appliedFilters) },
        allocationId,
        isClosed,
        isValid,
        isGlobalSearch: true,
        globalSearchValue: value,
      },
    };
    yield put(fetchAllocationFileSummaryAction); // Dispatch the action
    // message.success('Allocation records fetched successfully');
  } catch (error: any) {
    yield put(fetchAllocationRecordsByIdFailure(error?.message));
    message.error('Failed to fetch allocation records');
  }
}

// Saga for fetching allocation file metrics
function* fetchChannelPlannerMetricsSaga(
  action: PayloadAction<IChannelPlannerMetricsRequest>
) {
  try {
    const {
      columns,
      allocationId,
      isClosed = false,
      isValid = true,
    } = action.payload;
    const response: AxiosResponse<{ data: IChannelPlannerMetricsResponse }> =
      yield HTTPClient.PostRequest(
        `${allocationEndpoints.GET_CHANNLE_PLANNER_METRICS(
          allocationId,
          isClosed,
          isValid
        )}`,
        { columns }
      );
    yield put(fetchChannelPlannerMetricsSuccess(response.data?.data));
    // message.success('Allocation file summary fetched successfully');
  } catch (error: any) {
    yield put(fetchChannelPlannerMetricsFailure(error?.message));
    message.error('Failed to fetch channel planner metrics');
  }
}

// Saga for downloading excel file
function* downloadAllocationExcelFileSaga(
  action: PayloadAction<{ allocationId: string; isValid: boolean }>
) {
  try {
    const { allocationId, isValid } = action.payload;
    const response: AxiosResponse<{ data: { URL: string } }> =
      yield HTTPClient.GetRequest(
        `${allocationEndpoints.DOWNLOAD_ALLOCATION_EXCEL_FILE(
          allocationId,
          isValid
        )}`
      );
    yield put(downloadAllocationExcelFileSuccess(response.data.data.URL));
    // message.success('Allocation file summary fetched successfully');
  } catch (error: any) {
    yield put(downloadAllocationExcelFileFailure(error?.message));
    message.error('Failed to fetch channel planner metrics');
  }
}

// Saga for fetching auto-assign status
function* fetchAutoAssignStatusSaga(
  action: PayloadAction<{ allocationId: string }>
) {
  try {
    const { allocationId } = action.payload;
    const response: AxiosResponse<{ is_auto_allocated: boolean }> =
      yield HTTPClient.GetRequest(
        `${allocationEndpoints.GET_AUTO_ASSIGN_STATUS(allocationId)}`
      );

    yield put(fetchAutoAssignStatusSuccess(response.data));
    // if (response.data.is_auto_allocated) {
    //   message.success('Field has been auto-allocated.');
    // } else {
    //   message.info('Field has not been auto-allocated.');
    // }
  } catch (error: any) {
    // Log the error details for debugging

    // Check if the error is from Axios
    if (error?.response) {
      // Axios error response contains a 'data' field which might include error message/details
      const errorMessage =
        error?.response?.data?.message || 'An unknown error occurred';
      const errorDetails =
        error?.response?.data?.details || 'No additional details provided';

      // You can dispatch this info to the store or use it in your error message
      yield put(fetchAutoAssignStatusFailure(errorMessage));
      message.error(`${errorMessage}: ${errorDetails}`);
    } else {
      // For other errors (non-Axios), handle it here (e.g., network errors)
      yield put(
        fetchAutoAssignStatusFailure(
          error?.message || 'An unknown error occurred'
        )
      );
      message.error('Failed to fetch auto-assign status');
    }
  }
}

export function* watchAllocationDetailSagas() {
  yield takeLatest(
    fetchAllocationRecordsById.type,
    fetchAllocationRecordsByIdSaga
  );
  yield takeLatest(
    fetchAllocationFileHeaders.type,
    fetchAllocationFileHeadersSaga
  );
  yield takeLatest(fetchAllocationDashboard.type, fetchAllocationDashboardSaga);
  yield takeLatest(
    fetchRepeatAllocationDashboard.type,
    fetchRepeatAllocationDashboardSaga
  );
  yield takeLatest(
    fetchAllocationFileSummary.type,
    fetchAllocationFileSummarySaga
  );
  yield takeLatest(
    getUniqueValuesByColumnName.type,
    getUniqueValuesByColumnNameSaga
  );
  yield takeLatest(mergeHistoricData.type, mergeHistoricDataSaga);
  yield takeLatest(searchInFilterByValues.type, searchInFilterByValuesSaga);
  yield takeLatest(searchInDataGrid.type, searchInDataGridSaga);
  yield takeLatest(
    fetchChannelPlannerMetrics.type,
    fetchChannelPlannerMetricsSaga
  );
  yield takeLatest(
    downloadAllocationExcelFile.type,
    downloadAllocationExcelFileSaga
  );
  yield takeLatest(fetchAutoAssignStatus.type, fetchAutoAssignStatusSaga);
}
